import MojitoCore from 'mojito/core';

import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {isMobile} from '#core/utils/context-utils.js';

import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generateStreamingIndicator} from '#core/application/modules/tiles/streaming-indicator/index.js';
import {generatePrimaryButton, generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generateSecondaryDropdown} from '#core/application/modules/tiles/dropdown/index.js';
import {generateRunnerInfo} from '#core/application/modules/tiles/runner-info/index.js';
import {generateBestOddsGuaranteedIndicator} from '#core/application/modules/tiles/best-odds-guaranteed-indicator/index.js';

const merge = MojitoCore.Presentation.uiStyle.merge;

export function raceCardFactory(PALETTE) {
    const {FONT} = PALETTE;

    const RACE_CARD_DESCRIPTION_TEXT = {
        fontSize: FONT.SIZE_SMALL,
        ...PALETTE.RACE_CARD_DESCRIPTION_TEXT,
    };

    const TABLE_HEADER_COLUMN_COMMON = {
        // ...resolveBackground(PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_BG), temporarily leave bg empty because of Moj bug here
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
    };
    const RACE_TABLE_HEADER_COLUMN_COMMON = {
        ...TABLE_HEADER_COLUMN_COMMON,
        padding: '8px 0',
        textAlign: 'left',
        fontSize: FONT.SIZE_SMALL,
        ...PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_TEXT,
    };

    const TABLE_HEADER_SORT_BUTTON_TEXT = isMobile()
        ? PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_MOBILE_SORT_BUTTON_TEXT
        : PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_SORT_BUTTON_TEXT;

    const SORT_BUTTON = {
        style: {
            base: {
                ...TABLE_HEADER_COLUMN_COMMON,
                _mobile_margin: '0 8px 0 0',
                justifyContent: 'flex-end',
                fontSize: FONT.SIZE_SMALL,
                _mobile_fontSize: FONT.SIZE_MEDIUM,
                ...TABLE_HEADER_SORT_BUTTON_TEXT,
            },
            active: TABLE_HEADER_SORT_BUTTON_TEXT,
            hover: TABLE_HEADER_SORT_BUTTON_TEXT,
            // we need to somehow mark selected sort field, in Zeplin it's done by rendering sort icon near selected field, but
            // Moj render this icon for all sort buttons and that's why we use font-weight bold for these purposes
            disabled: {
                ...RACE_TABLE_HEADER_COLUMN_COMMON,
                cursor: 'initial',
                padding: '8px',
            },
        },
    };

    const CAST_MARKET_SELECTION_BUTTON_BASE = {
        fontSize: FONT.SIZE_MEDIUM,
        fontWeight: FONT.WEIGHT_SEMI_BOLD,
        borderStyle: 'none',
        borderRadius: PALETTE.RACING_ODDS_BUTTON.ODDS_BORDER_RADIUS,
        overflow: 'hidden',
    };
    const CAST_MARKET_SELECTION_BUTTON_TEXT = {
        style: {
            container: {
                // workaround to make text-overflow works (72 - maxWidth of parent, 16 - right and left padding = (72 - 16 = 56))
                // needed because of moj strange styles for FlexPane inside SelectableButton
                maxWidth: '56px',
            },
        },
    };

    return {
        RaceCard: {
            castMenuContainer: {
                style: {
                    _mobile_overflow: 'auto hidden',
                },
            },
            raceCardContainer: {
                style: {
                    ...resolveBackground(PALETTE.RACE_CARD_CONTAINER_BG),
                    boxShadow: PALETTE.RACE_CARD_CONTAINER_SHADOW,
                    borderRadius: PALETTE.RACE_CARD_CONTAINER_BORDER_RADIUS,
                    padding: 0,
                },
            },
            // markets
            marketsContainer: {
                style: {
                    minHeight: '0px',
                    padding: '8px',
                },
            },
            itemContainer: {
                selectableButton: generateToggleButton(PALETTE.RACE_CARD_MARKETS_BUTTON, true),
            },
            castMenuDropdown: generateSecondaryDropdown(PALETTE.RACE_CARD_MARKETS_DROPDOWN),
            raceOffContainer: {
                style: {
                    width: '100%',
                },
            },
            raceOffContainerText: {
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    ...PALETTE.RACE_CARD_RACE_OFF_TEXT,
                },
            },
            // description
            raceCardDescription: {
                descriptionContainer: {
                    style: {
                        padding: '8px 8px 16px 8px',
                    },
                },
                description: {
                    style: {
                        ...RACE_CARD_DESCRIPTION_TEXT,
                    },
                },
            },
            finishedRaceDescription: {
                description: {
                    style: {
                        padding: '8px 8px 0 8px',
                        ...RACE_CARD_DESCRIPTION_TEXT,
                    },
                },
            },
            // runners table
            headerContainer: {
                style: {
                    padding: '8px 0',
                },
            },
            runnersListHeaderContainer: {
                style: {
                    padding: '8px 0 0',
                },
            },
            runnersGrid: {
                style: {
                    padding: '8px',
                    _mobile_padding: '0 0 8px',
                },
            },
            sortToggleButton: SORT_BUTTON,
            sortByNumberButton: SORT_BUTTON,
            sortByPriceButton: SORT_BUTTON,
            // sort button rotation icon
            rotation: {
                image: icon(
                    PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_SORT_BUTTON_ICON_COLOR,
                    PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_SORT_BUTTON_ICON_SRC
                ),
            },
            previousOddsHeaderContainer: {
                style: RACE_TABLE_HEADER_COLUMN_COMMON,
            },
            namesHeaderContainer: {
                container: {
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '0',
                    textAlign: 'left',
                    fontSize: FONT.SIZE_SMALL,
                    ...PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_HEADER_TEXT,
                },
            },
            formHeaderContainer: {
                style: RACE_TABLE_HEADER_COLUMN_COMMON,
            },
            ageAndWeightHeaderContainer: {
                style: RACE_TABLE_HEADER_COLUMN_COMMON,
            },
            eachWayContainer: {
                container: {
                    width: '100%',
                },
                style: {
                    ...RACE_TABLE_HEADER_COLUMN_COMMON,
                    _desktop_backgroundColor: 'transparent',
                    _desktop_backgroundImage: 'none',
                    textAlign: 'right',
                    _desktop_padding: 0,
                    _mobile_padding: '8px',
                },
            },
            eachWayContainerEventFinished: {
                container: {
                    width: '100%',
                },
                style: {
                    ...RACE_TABLE_HEADER_COLUMN_COMMON,
                    padding: '8px 0 8px 8px',
                },
            },
            runnerInfo: merge(generateRunnerInfo(PALETTE.RACE_CARD_RUNNERS_TABLE_RUNNER_INFO), {
                numbersAndSilkContainer: {
                    style: {
                        padding: '0 8px 0 0',
                        _mobile_padding: '0 8px 0',
                    },
                },
                runnerNumberContainer: {
                    style: {
                        padding: '0 8px',
                    },
                },
            }),
            runnerRows: {
                default: {
                    bottomSeparator: {
                        style: {
                            backgroundColor: PALETTE.RACE_CARD_RUNNERS_TABLE.TABLE_ITEM_DIVIDER_COLOR,
                            margin: '8px 0',
                        },
                    },
                },
                last: {
                    bottomSeparator: {
                        style: {
                            size: 0,
                        },
                    },
                },
            },
            runnerResult: {
                runnerPositionText: {
                    style: {
                        padding: '0 0 0 8px',
                        fontSize: FONT.SIZE_MEDIUM,
                        ...PALETTE.RUNNER_POSITION_TEXT,
                    },
                },
                runnerDetails: {
                    style: {
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_SEMI_BOLD,
                        ...PALETTE.RUNNER_DETAILS_TEXT,
                    },
                },
                runnerPriceText: {
                    style: {
                        fontSize: FONT.SIZE_MEDIUM,
                        fontWeight: FONT.WEIGHT_BOLD,
                        ...PALETTE.RUNNER_PRICE_TEXT,
                    },
                },
                runnerSilk: {
                    style: {
                        height: '24px',
                        width: '32px',
                        padding: '0 8px 0 4px',
                    },
                },
            },
            // non runners and deductions
            style: {
                raceCardInfoSeparator: {
                    backgroundColor: PALETTE.RACE_CARD_SEPARATOR_COLOR,
                },
                nonRunnersContainer: {
                    padding: '16px 8px 8px',
                },
                nonRunnersLabel: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.RACE_CARD_NON_RUNNERS_LABEL_TEXT,
                },
                nonRunnersNames: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    paddingLeft: 4,
                    ...PALETTE.RACE_CARD_NON_RUNNERS_NAMES_LIST_TEXT,
                },
                deductionsHeader: {
                    fontSize: 12,
                    fontWeight: FONT.WEIGHT_BOLD,
                    padding: '8px 8px 8px',
                    ...PALETTE.RACE_CARD_DEDUCTIONS_TITLE_TEXT,
                },
                deductions: {
                    fontSize: 12,
                    ...PALETTE.RACE_CARD_DEDUCTIONS_LIST_TEXT,
                },
            },
            // cast markets table
            castMarketsGridPane: {
                style: {
                    _mobile_gridTemplateColumns: '60% 20% 20%',
                    gridTemplateColumns: '70% 15% 15%',
                },
            },
            castMarketsHeader: {
                container: {
                    ...resolveBackground(PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_HEADER_BG),
                },
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_HEADER_TEXT,
                },
            },
            castMarketsHeaderLastColumn: {
                container: {
                    ...resolveBackground(PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_HEADER_BG),
                },
                style: {
                    fontSize: FONT.SIZE_SMALL,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_HEADER_TEXT,
                },
            },
            marketsDataFirstColumn: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_ITEM_TEXT,
                },
            },
            marketsDataSecondColumn: {
                style: {
                    textAlign: 'center',
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_ITEM_TEXT,
                },
            },
            marketsDataThirdColumn: {
                style: {
                    textAlign: 'end',
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.RACE_CARD_CAST_MARKETS_TABLE.TABLE_ITEM_TEXT,
                },
            },
            // other
            addToBetslipButton: generatePrimaryButton(PALETTE.RACE_CARD_ADD_TO_BETSLIP_BUTTON),
            // quantum needs a custom runnerInfoToShowOnVirtuals since virtual sports have a "virtual_" prefix there
            runnerInfoToShowOnVirtuals: !GENBET_BASED
                ? {
                      virtual_greyhound_racing: {
                          ageWeight: false,
                          previousOdds: false,
                          form: false,
                      },
                      virtual_horse_racing: {
                          ageWeight: false,
                          previousOdds: false,
                          form: true,
                      },
                      virtual_cycling: {
                          ageWeight: false,
                          previousOdds: false,
                          form: false,
                      },
                      virtual_speedway: {
                          ageWeight: false,
                          previousOdds: false,
                          form: false,
                      },
                      virtual_trotting: {
                          ageWeight: false,
                          previousOdds: false,
                          form: false,
                      },
                  }
                : {},
        },
        // info
        RaceCardInfo: {
            header: {
                style: {
                    ...resolveBackground(PALETTE.RACE_CARD_INFO_HEADER_BG),
                    borderWidth: '0 0 1px',
                    borderColor: PALETTE.RACE_CARD_SEPARATOR_COLOR,
                    _desktop_borderStyle: 'solid',
                    borderRadius: `${PALETTE.RACE_CARD_INFO_HEADER_BORDER_RADIUS}px ${PALETTE.RACE_CARD_INFO_HEADER_BORDER_RADIUS}px 0px 0px`,
                },
            },
            headerTitleButton: {
                style: {
                    base: {
                        overflow: 'hidden',
                        fontSize: FONT.SIZE_LARGE,
                        fontWeight: FONT.WEIGHT_BOLD,
                        maxWidth: 512, // x2 from mojito min-width
                        ...PALETTE.RACE_CARD_INFO_TITLE_TEXT,
                    },
                },
            },
            headerTitleContainer: {
                style: {
                    maxWidth: '100%',
                },
            },
            streamingIndicator: generateStreamingIndicator(PALETTE.RACE_CARD_INFO_STREAMING_INDICATOR),
            raceLinks: generatePivotsRow(PALETTE.RACE_CARD_INFO_RACE_TIMES_ROW),
            firstRow: {
                style: {
                    fontSize: FONT.SIZE_XSMALL,
                    padding: '0 0 4px 0',
                    ...PALETTE.RACE_CARD_INFO_RACE_NUMBER_TEXT,
                },
            },
            secondRow: {
                style: {
                    fontSize: FONT.SIZE_XSMALL,
                    padding: '0 0 4px 0',
                    ...PALETTE.RACE_CARD_INFO_DISTANCE_TEXT,
                },
            },
            thirdRow: {
                style: {
                    fontSize: FONT.SIZE_XSMALL,
                    ...PALETTE.RACE_CARD_INFO_RUNNERS_AMOUNT_TEXT,
                },
            },
            countdownTime: {
                timerFourDigits: {
                    timeBox: {
                        style: {
                            minWidth: 'initial',
                        },
                    },
                    timePart: {
                        style: {
                            // silly hack to prevent timer from jumping, same as for RaceMeetingCoupon
                            minWidth: 10,
                        },
                    },
                },
            },
            bestOddsGuaranteedPopup: generateBestOddsGuaranteedIndicator(
                PALETTE.RACE_CARD_BEST_ODDS_GUARANTEED_INDICATOR
            ),
        },

        CastMarketSelectionButtons: {
            container: {
                style: {
                    padding: '0 8px',
                },
            },
            selectionButton: {
                selected: {
                    style: {
                        base: {
                            ...resolveBackground(PALETTE.RACING_ODDS_BUTTON.ODDS_SELECTED_BG),
                            ...CAST_MARKET_SELECTION_BUTTON_BASE,
                            ...PALETTE.RACING_ODDS_BUTTON.ODDS_SELECTED_PRICE_TEXT,
                        },
                    },
                },
                unselected: {
                    style: {
                        base: {
                            ...resolveBackground(PALETTE.RACING_ODDS_BUTTON.ODDS_BG),
                            ...CAST_MARKET_SELECTION_BUTTON_BASE,
                            ...PALETTE.RACING_ODDS_BUTTON.ODDS_PRICE_TEXT,
                        },
                    },
                },
                indicator: {
                    style: {
                        base: {
                            height: 0,
                            backgroundColor: null,
                        },
                    },
                },
            },
            nonRunnerButton: CAST_MARKET_SELECTION_BUTTON_TEXT,
            selectionButtonText: CAST_MARKET_SELECTION_BUTTON_TEXT,
        },
        RunnerSilk: {
            icon: {
                fallbackSrc: 'racing/silk_default.png',
            },
            // quantum needs a custom silkSrcMap since virtual sports have a "virtual_" prefix there
            silkSrcMap: !GENBET_BASED
                ? {
                      virtual_horse_racing: 'racing/virtual/horse/silk%NUMBER%.svg',
                      virtual_greyhound_racing: 'racing/virtual/dog/silk%NUMBER%.svg',
                      virtual_cycling: 'racing/virtual/cycling/silk%NUMBER%.svg',
                      virtual_speedway: 'racing/virtual/speedway/silk%NUMBER%.svg',
                      virtual_trotting: 'racing/virtual/trotting/silk%NUMBER%.svg',
                  }
                : {},
        },
        RaceCardPreview: {
            bestOddsIcon: icon(PALETTE.RACE_CARD_PREVIEW_BEST_ODDS_RACE_ICON_COLOR),
        },
    };
}
